import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { TopHeaderForBuyer } from 'ui/components/panes/Header/TopHeaderForBuyer';
import { TopFooter } from 'ui/components/panes/Footer/TopFooter';
import { KvPictures } from 'ui/components/public/patterns/TopForBuyer/KvPictures';
import { KvMainText } from 'ui/components/public/patterns/TopForBuyer/KvMainText';
import { SubTitleSection } from 'ui/components/public/patterns/TopForBuyer/SubTitleSection';
import { Announcements } from 'ui/components/public/patterns/TopForBuyer/Announcements';
import { AboutService } from 'ui/components/public/patterns/TopForBuyer/AboutService';
import { Interview } from 'ui/components/public/patterns/TopForBuyer/Interview';
import { Issue } from 'ui/components/public/patterns/TopForBuyer/Issue';
// import { Cases } from 'ui/components/public/patterns/Top/Cases';
import { QA } from 'ui/components/public/patterns/TopForBuyer/QA';
import { Offer } from 'ui/components/public/patterns/TopForBuyer/Offer';
import KvPcImg from 'ui/images/top_for_buyer/kv_bg_pc.svg';
import KvSpImg from 'ui/images/top_for_buyer/kv_bg_sp.svg';
import BgRibbonPcImg from 'ui/images/top_for_buyer/bg_ribbon_pc.svg';
import BgRibbonSpImg from 'ui/images/top_for_buyer/bg_ribbon_sp.svg';
import BgCheckPcImg from 'ui/images/top_for_buyer/bg_check_pc.svg';
import { mqMin } from 'utils/responsive';
import { Only } from 'ui/components/elements/Only/Only';
import { useLocation } from 'react-router-dom';

const Wrapper = styled.div`
    font-weight: 700;
`;

const MainSection = styled.section`
    position: relative;
    width: 100%;
    background-color: var(--light-orange);
    padding-bottom: 31rem;
    overflow: hidden;

    ${mqMin('NARROW')} {
        width: 100%;
        padding-top: 20px;
        padding-bottom: 7rem;
        overflow: hidden;
    }

    img {
        display: block;
        max-width: 100%;
    }

    @keyframes kvFadein {
        0% {
            opacity: 0;
        }
        100% {
            opacity: 1;
        }
    }
    @keyframes kvFadeinY {
        0% {
            opacity: 0;
            translate: -50% 25px;
        }
        100% {
            opacity: 1;
            translate: -50% 0;
        }
    }

    @keyframes kvFadeinZoom {
        0% {
            opacity: 0;
            scale: 0.9;
        }
        100% {
            opacity: 1;
            scale: 1;
        }
    }
`;

const CheckOverlay = styled.div`
    display: none;

    ${mqMin('NARROW')} {
        position: absolute;
        top: 0;
        width: 100%;
        height: 100%;
        display: block;
        background-image: url(${BgCheckPcImg});
        background-repeat: no-repeat;
        background-size: 100% auto;
        background-position: top 4rem center;
        z-index: 1;
        left: 50%;
        transform: translateX(-50%);
    }

    ${mqMin('WIDE')} {
        background-position: top 2rem center;
    }
`;

const RibbonOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url(${BgRibbonSpImg});
    background-repeat: no-repeat;
    background-size: contain;
    background-position: top 15rem center;
    pointer-events: none;
    z-index: 1;

    ${mqMin('NARROW')} {
        background-image: url(${BgRibbonPcImg});
        background-size: 100% auto;
        background-position: top 16rem center;
        margin: 0 auto;
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        width: 100%;
    }
`;

const BgImg = styled.img`
    width: 100%;
    position: absolute;
    top: 9rem;

    ${mqMin('NARROW')} {
        max-width: 1511px;
        bottom: 2.6rem;
        top: auto;
        transform: none;
    }

    ${mqMin('WIDE')} {
        bottom: -8rem;
    }
`;

export const TopForBuyer: React.FC = () => {
    const [ticking, setTicking] = useState(false);
    const fadeItems = useRef<NodeListOf<Element> | null>(null);
    const fadeItemsLi = useRef<NodeListOf<Element> | null>(null);
    const location = useLocation();

    useEffect(() => {
        // topページにのみ適用するcssを追加
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = '/top.css';
        document.head.appendChild(link);

        return () => {
            document.head.removeChild(link);
        };
    }, []);

    useEffect(() => {
        // フェードイン処理
        fadeItems.current = document.querySelectorAll('.fadein');
        fadeItemsLi.current = document.querySelectorAll('.fadeinLi');

        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            const fadePoint = 350;

            fadeItems.current?.forEach((e) => {
                const itemTop = e.getBoundingClientRect().top + scrollTop;
                if (
                    scrollTop + windowHeight - fadePoint > itemTop ||
                    scrollTop + windowHeight >= documentHeight - 100
                ) {
                    e.classList.add('scroll_on');
                }
            });

            fadeItemsLi.current?.forEach((e) => {
                const itemTop = e.getBoundingClientRect().top + scrollTop;
                const childfadeItems = Array.from(e.children);
                if (scrollTop + fadePoint > itemTop) {
                    childfadeItems.forEach((elm, i) => {
                        setTimeout(() => {
                            elm.classList.add('scroll_on');
                        }, 300 * i);
                    });
                }
            });
        };

        function onScroll() {
            if (!ticking) {
                requestAnimationFrame(handleScroll);
                setTicking(true);
            }
        }

        window.addEventListener('scroll', onScroll);
        handleScroll();

        return () => {
            window.removeEventListener('scroll', onScroll);
        };
    }, []);

    useEffect(() => {
        if (location.hash) {
            const targetId = location.hash.replace('#', '');
            const el = document.getElementById(targetId);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }, [location.hash]);

    return (
        <Wrapper>
            <TopHeaderForBuyer />
            <MainSection>
                <CheckOverlay />
                <RibbonOverlay />
                <Only sp>
                    <BgImg src={KvSpImg} alt="背景画像" />
                </Only>
                <Only pc>
                    <BgImg src={KvPcImg} alt="背景画像" />
                </Only>
                <KvPictures />
                <KvMainText />
            </MainSection>
            <main id="home">
                <Announcements />
                <SubTitleSection />
                <AboutService />
                <Issue />
                <Interview />
                {/* <Cases /> */}
                <QA />
                <Offer />
            </main>
            <TopFooter />
        </Wrapper>
    );
};
